import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../../lib/Icons';
import { getSencoProvisionOverview } from '../../lib/functions';

function daysWaiting(isoDate) {
  if (!isoDate) return null;
  const start = new Date(isoDate).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((Date.now() - start) / 86400000));
}

export default function SencoProvisionAlerts() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [waitlistFilter, setWaitlistFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getSencoProvisionOverview({});
      setPayload(result.data || null);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not load provision overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const waitlistTypes = useMemo(() => {
    if (!payload?.waitlistByType) return [];
    return Object.keys(payload.waitlistByType).sort();
  }, [payload]);

  const filteredWaitlist = useMemo(() => {
    const entries = payload?.waitlistEntries || [];
    if (waitlistFilter === 'all') return entries;
    return entries.filter((e) => e.interventionType === waitlistFilter);
  }, [payload, waitlistFilter]);

  const missingStudents = payload?.missingProvisionStudents || [];

  return (
    <section className="rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
      <div className="border-b border-gray-100 p-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Provision & waitlist</h2>
          <p className="mt-1 text-sm text-gray-600">
            EHCP Section F gaps and intervention queues across the school.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Icons.ArrowPath className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="p-5 space-y-6">
        {loading && (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Icons.ArrowPath className="h-4 w-4 animate-spin" />
            Loading provision alerts...
          </p>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && payload && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Missing provision */}
              <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icons.ExclamationTriangle className="h-5 w-5 text-amber-600" />
                  <h3 className="text-sm font-semibold text-gray-900">Section F not yet met</h3>
                </div>
                {missingStudents.length === 0 ? (
                  <p className="text-sm text-gray-500">No unmet Section F entitlements. All recorded provision matches EHCP.</p>
                ) : (
                  <ul className="space-y-3 max-h-80 overflow-y-auto">
                    {missingStudents.slice(0, 12).map((student) => (
                      <li key={student.studentId} className="rounded-lg border border-amber-100 bg-white p-3 text-sm">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <Link
                              to={student.route}
                              className="font-medium text-gray-900 hover:text-indigo-600"
                            >
                              {student.studentName}
                            </Link>
                            <p className="text-xs text-gray-500">
                              {student.yearGroup || 'Year unknown'}
                              {student.hasEhcp ? ' • EHCP' : ''}
                            </p>
                          </div>
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            {student.unmetCount} unmet
                            {student.partialCount > 0 ? `, ${student.partialCount} waiting` : ''}
                          </span>
                        </div>
                        <ul className="mt-2 space-y-1">
                          {student.items.slice(0, 3).map((item) => (
                            <li key={item.id} className="text-xs text-gray-600 line-clamp-2">
                              • {item.sourceText}
                              {item.fulfillmentStatus === 'partial' ? ' (on waitlist)' : ''}
                            </li>
                          ))}
                          {student.items.length > 3 && (
                            <li className="text-xs text-gray-400">+{student.items.length - 3} more</li>
                          )}
                        </ul>
                        <Link
                          to={student.route}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline"
                        >
                          Review provision
                          <Icons.ArrowTopRightOnSquare className="h-3.5 w-3.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* School-wide waitlist */}
              <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Icons.Clock className="h-5 w-5 text-purple-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Intervention waitlist</h3>
                  </div>
                  {waitlistTypes.length > 1 && (
                    <select
                      value={waitlistFilter}
                      onChange={(e) => setWaitlistFilter(e.target.value)}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
                    >
                      <option value="all">All types</option>
                      {waitlistTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  )}
                </div>
                {filteredWaitlist.length === 0 ? (
                  <p className="text-sm text-gray-500">No pupils currently waiting for intervention places.</p>
                ) : (
                  <ul className="space-y-3 max-h-80 overflow-y-auto">
                    {filteredWaitlist.slice(0, 15).map((entry) => {
                      const days = daysWaiting(entry.waitingSince);
                      return (
                        <li key={entry.id} className="rounded-lg border border-purple-100 bg-white p-3 text-sm">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <span className="text-xs font-semibold text-purple-700">#{entry.queuePosition}</span>
                              {' '}
                              <Link
                                to={entry.route}
                                className="font-medium text-gray-900 hover:text-indigo-600"
                              >
                                {entry.studentName}
                              </Link>
                              <p className="text-xs text-gray-500">
                                {entry.yearGroup || ''}
                                {entry.hasEhcp ? ' • EHCP' : ''}
                                {days != null ? ` • ${days}d waiting` : ''}
                              </p>
                            </div>
                            <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                              {entry.interventionType}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{entry.suggestedName || entry.sourceText}</p>
                          {entry.priorityReason && (
                            <p className="text-xs text-purple-700 mt-1">{entry.priorityReason}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <Link
                  to="/interventions"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline"
                >
                  Manage interventions & swap suggestions
                  <Icons.ArrowTopRightOnSquare className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
        )}
      </div>
    </section>
  );
}
